import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import { AppState } from 'app/types';
import Playlist from 'entity/Playlist';
import Paging from 'rest/Paging';

import { NONE_PLAYLIST, NONE_PLAYLIST_ID, PlaylistService } from '../services/playlist.service';
import { VideoChatService } from '../services/videochat.service';

export interface ModalMusicPlayListData {
  modalTitle: string;
  primaryButtonText: string;
}

@Component({
  selector: 'app-modal-music-playlist',
  templateUrl: './modal-music-playlist.component.html',
  styleUrls: ['./modal-music-playlist.component.scss'],
  standalone: false,
})
export class ModalMusicPlayListComponent implements OnInit, OnDestroy {
  private data = inject<ModalMusicPlayListData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<ModalMusicPlayListComponent>>(MatDialogRef);
  private matIconRegistry = inject(MatIconRegistry);
  private videoChatService = inject(VideoChatService);
  private playlistService = inject(PlaylistService);
  private store = inject<Store<AppState>>(Store);

  public playlist: UntypedFormControl;
  public playlistOptions?: Playlist[];
  public playlistSelectedName?: string;
  public showLoader = true;

  private geoMusicAllowed?: boolean;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    const domSanitizer = inject(DomSanitizer);

    this.playlist = new UntypedFormControl();

    // Subscribe to Playlist Change
    this.playlistService.playlistChanged$.pipe(takeUntil(this.destroyed$)).subscribe((playlist) => {
      if (playlist && playlist.playlistId) {
        this.playlist.setValue(playlist.playlistId);
        this.playlistSelectedName = this.getPlaylistNameById(playlist.playlistId);
      }
    });

    this.matIconRegistry.addSvgIcon(
      'playlist_premium',
      domSanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/custom-icons/playlist-premium.svg'),
    );

    this.store
      .select(coreSelectors.getGeoMusicAllowed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowed) => (this.geoMusicAllowed = allowed));
  }

  public async ngOnInit(): Promise<void> {
    await this.getPlaylists();

    // Detect Changes
    this.playlist.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((newPlaylistId) => {
      this.playlistSelectedName = this.getPlaylistNameById(newPlaylistId);
    });

    // Reset Loader
    this.showLoader = false;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async getPlaylists(): Promise<void> {
    this.showLoader = true;
    const paging = new Paging(0, 100);
    const playlists = (await this.videoChatService.getAllPlaylists(paging)) ?? [];

    playlists.sort((a, b) => {
      if (a.type === undefined) {
        return 1;
      }
      if (b.type === undefined) {
        return -1;
      }

      return a.type > b.type ? -1 : a.type < b.type ? 1 : 0;
    });

    this.playlistOptions = [NONE_PLAYLIST, ...playlists];

    this.playlist = new UntypedFormControl(this.playlistService.playlistId ?? NONE_PLAYLIST_ID);
    this.playlistSelectedName = this.getPlaylistNameById(this.playlistService.playlistId) ?? NONE_PLAYLIST.name;
  }

  public closeDialog(): void {
    this.dialogRef.close(null);
  }

  public async updatePlaylist(): Promise<void> {
    if (this.playlistService.playlistId !== this.playlist.value) {
      this.dialogRef.close(this.playlist.value);
      await this.playlistService.changeRoomPlaylist(this.playlist.value);
    }
  }

  public getPlaylistNameById(playlistId: string): string {
    const playlistSelectedFound = this.playlistOptions?.find((playlist) => playlist.playlistId === playlistId);
    return playlistSelectedFound?.name ?? '-';
  }
}
